/**
 * NHG werkgeversverklaring (lane O1) — types, DTO's en Zod-schemas.
 *
 * Spiegelt de Prisma-enums (Werkgeversverklaring*, Wgv*) als string-tuples +
 * NL-talige label-records zodat de UI labels heeft zonder Prisma te importeren.
 * DTO's zijn een afspiegeling van Prisma-rijen + computed velden; geld wordt als
 * `string` (Decimal serialized) geretourneerd, en salaris/lening/loonbeslag-velden
 * zijn optioneel/nullable zodat de afschermlaag ze serverside kan weglaten.
 *
 * Conventies: geld nooit als Float; ID-refs valideren via `idParam`/`.min(1)`
 * (prod heeft legacy UUID-format ids, dus géén `.cuid()` op refs); merknaam
 * STREEP; geen em-dash in zichtbare tekst.
 */

import { z } from 'zod'
import { idParam } from './id-validators'
// batch-62 briefpapier (FO-459) - de briefpapierkeuze bij generatie.
import { BriefpapierKeuzeInvoerSchema } from './briefpapier'

// ─── Enums (matching Prisma enums) ────────────────────────────────────────────

export const WGV_STATUS = [
  'CONCEPT',
  'IN_CONTROLE',
  'WACHT_OP_AANVULLING',
  'KLAAR_VOOR_ONDERTEKENING',
  'ONDERTEKEND',
  'VERSTREKT',
  'GEANNULEERD',
  'VERLOPEN_VERVANGEN',
] as const
export type WgvStatus = (typeof WGV_STATUS)[number]

export const WGV_VELD_BRON = [
  'AUTOMATISCH',
  'BEREKEND',
  'HANDMATIG',
  'AANGEPAST',
  'ONTBREEKT',
  'GECONTROLEERD',
] as const
export type WgvVeldBron = (typeof WGV_VELD_BRON)[number]

export const WGV_VOORTZETTING = [
  'JA_ONBEPAALDE',
  'JA_BEPAALDE',
  'NEE',
  'NOG_TE_BEOORDELEN',
] as const
export type WgvVoortzetting = (typeof WGV_VOORTZETTING)[number]

export const WGV_ONDERTEKEN_METHODE = ['HANDMATIG', 'ELEKTRONISCH'] as const
export type WgvOndertekenMethode = (typeof WGV_ONDERTEKEN_METHODE)[number]

export const WGV_VERSTREK_KANAAL = ['DOWNLOAD', 'EMAIL', 'PORTAL'] as const
export type WgvVerstrekKanaal = (typeof WGV_VERSTREK_KANAAL)[number]

export const WGV_AANVRAAG_DOEL = ['HYPOTHEEK', 'HUUR', 'LENING', 'ANDERS'] as const
export type WgvAanvraagDoel = (typeof WGV_AANVRAAG_DOEL)[number]

export const WGV_AUDIT_ACTIE = [
  'AANGEMAAKT',
  'AUTO_GEVULD',
  'SALARIS_BEKEKEN',
  'HANDMATIG_GEWIJZIGD',
  'GEGENEREERD',
  'GEDOWNLOAD',
  'ONDERTEKEND',
  'GEDEELD_MEDEWERKER',
  'GEANNULEERD',
  'VERWIJDERD',
] as const
export type WgvAuditActie = (typeof WGV_AUDIT_ACTIE)[number]

// ─── Label constants (NL-talig voor UI) ───────────────────────────────────────

export const WGV_STATUS_LABELS: Record<WgvStatus, string> = {
  CONCEPT:                  'Concept',
  IN_CONTROLE:              'In controle',
  WACHT_OP_AANVULLING:      'Wacht op aanvulling',
  KLAAR_VOOR_ONDERTEKENING: 'Klaar voor ondertekening',
  ONDERTEKEND:              'Ondertekend',
  VERSTREKT:                'Verstrekt',
  GEANNULEERD:              'Geannuleerd',
  VERLOPEN_VERVANGEN:       'Verlopen of vervangen',
}

export const WGV_VELD_BRON_LABELS: Record<WgvVeldBron, string> = {
  AUTOMATISCH:   'Automatisch gevuld',
  BEREKEND:      'Berekend',
  HANDMATIG:     'Handmatig ingevoerd',
  AANGEPAST:     'Handmatig aangepast',
  ONTBREEKT:     'Ontbreekt',
  GECONTROLEERD: 'Gecontroleerd',
}

export const WGV_VOORTZETTING_LABELS: Record<WgvVoortzetting, string> = {
  JA_ONBEPAALDE:    'Ja, voor onbepaalde tijd',
  JA_BEPAALDE:      'Ja, voor bepaalde tijd',
  NEE:              'Nee',
  NOG_TE_BEOORDELEN: 'Nog te beoordelen',
}

export const WGV_ONDERTEKEN_METHODE_LABELS: Record<WgvOndertekenMethode, string> = {
  HANDMATIG:    'Handmatig (printen, tekenen, scannen)',
  ELEKTRONISCH: 'Elektronisch ondertekenen',
}

export const WGV_VERSTREK_KANAAL_LABELS: Record<WgvVerstrekKanaal, string> = {
  DOWNLOAD: 'Download',
  EMAIL:    'E-mail',
  PORTAL:   'Portal',
}

export const WGV_AANVRAAG_DOEL_LABELS: Record<WgvAanvraagDoel, string> = {
  HYPOTHEEK: 'Hypotheek',
  HUUR:      'Huurwoning',
  LENING:    'Lening',
  ANDERS:    'Anders',
}

export const WGV_AUDIT_ACTIE_LABELS: Record<WgvAuditActie, string> = {
  AANGEMAAKT:         'Aangemaakt',
  AUTO_GEVULD:        'Automatisch gevuld',
  SALARIS_BEKEKEN:    'Salaris bekeken',
  HANDMATIG_GEWIJZIGD: 'Handmatig gewijzigd',
  GEGENEREERD:        'Gegenereerd',
  GEDOWNLOAD:         'Gedownload',
  ONDERTEKEND:        'Ondertekend',
  GEDEELD_MEDEWERKER: 'Gedeeld met medewerker',
  GEANNULEERD:        'Geannuleerd',
  VERWIJDERD:         'Verwijderd',
}

// ─── Zod-schemas voor request bodies ──────────────────────────────────────────
//
// IDs valideren via `idParam` (cuid OF legacy-UUID OF seed-id) — NOOIT `.cuid()`,
// want prod bevat veel UUID-format ids. Geld wordt als `z.number().nonnegative()`
// geaccepteerd en door de lib/route naar `Prisma.Decimal` geconverteerd.

/** Geldbedrag-input: niet-negatief getal; route converteert naar Prisma.Decimal. */
const moneyInput = z.number().nonnegative({ message: 'Bedrag mag niet negatief zijn' })
const moneyInputNullable = moneyInput.nullable()

/** ISO-date string (YYYY-MM-DD); route parseert naar UTC-datum. */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet YYYY-MM-DD zijn')

/** POST /werkgeversverklaringen — aanmaken (auto-fill volgt serverside). */
export const CreateWerkgeversverklaringSchema = z.object({
  employeeId: idParam('employeeId'),
  /** Optionele override van de werkgever-BV (default = BV op actief contract). */
  employerEntityId: idParam('employerEntityId').optional(),
  /** Optionele koppeling aan een portal-aanvraag. */
  requestId: idParam('requestId').optional(),
  /** Optionele peildatum voor salaris/contract (default = vandaag). */
  peildatum: isoDate.optional(),
})
export type CreateWerkgeversverklaringInput = z.infer<typeof CreateWerkgeversverklaringSchema>

/**
 * PATCH /werkgeversverklaringen/:id — bewerkbare contract-/inkomen-/lening-/
 * loonbeslag-/ondertekenaar-velden. Elke handmatige wijziging van een
 * auto/berekend veld vereist een `reden` (Deel 8); de route schrijft die naar
 * de WgvVeldSnapshot + WgvAuditLog. `reden` is één veld voor de hele mutatie.
 */
export const UpdateWgvVeldenSchema = z
  .object({
    // --- Aard dienstverband ---
    contractEinddatum:        isoDate.nullable().optional(),
    isFlexibel:               z.boolean().optional(),
    flexibelToelichting:      z.string().trim().max(500).nullable().optional(),
    proeftijd:                z.boolean().optional(),
    proeftijdVerstreken:      z.boolean().nullable().optional(),
    reorganisatieVoornemen:   z.boolean().optional(),
    reorganisatieToelichting: z.string().trim().max(2000).nullable().optional(),
    dgaJaNee:                 z.boolean().optional(),
    dgaPercentage:            z.number().min(0).max(100).nullable().optional(),

    // --- Voortzetting dienstverband ---
    voortzetting:           z.enum(WGV_VOORTZETTING).nullable().optional(),
    voortzettingMinMaanden: z.number().int().min(0).max(600).nullable().optional(),
    voorwaardenGewijzigd:   z.boolean().optional(),
    voorwaardenToelichting: z.string().trim().max(2000).nullable().optional(),

    // --- Inkomen (gevoelig) ---
    brutoMaandsalaris:        moneyInputNullable.optional(),
    brutoJaarsalaris:         moneyInputNullable.optional(),
    vakantietoeslag:          moneyInputNullable.optional(),
    dertiendeMaand:           moneyInputNullable.optional(),
    eindejaarsuitkering:      moneyInputNullable.optional(),
    onregelmatigheidstoeslag: moneyInputNullable.optional(),
    structureelOverwerk:      moneyInputNullable.optional(),
    structureleProvisie:      moneyInputNullable.optional(),
    structureleReisuren:      moneyInputNullable.optional(),
    flexibelBudget:           moneyInputNullable.optional(),

    // --- Leningen en loonbeslag (gevoelig) ---
    werkgeversleningJaNee:    z.boolean().optional(),
    leningIngangsdatum:       isoDate.nullable().optional(),
    leningHoofdsom:           moneyInputNullable.optional(),
    leningLooptijdMaanden:    z.number().int().min(0).max(600).nullable().optional(),
    leningMaandlast:          moneyInputNullable.optional(),
    loonbeslagJaNee:          z.boolean().optional(),
    loonbeslagBedragPerMaand: moneyInputNullable.optional(),
    loonbeslagToelichting:    z.string().trim().max(2000).nullable().optional(),

    // --- Ondertekening / verificatie ---
    ondertekenaarNaam:      z.string().trim().max(200).nullable().optional(),
    ondertekenaarUserId:    idParam('ondertekenaarUserId').nullable().optional(),
    plaatsOndertekening:    z.string().trim().max(120).nullable().optional(),
    verificatieContactNaam: z.string().trim().max(200).nullable().optional(),
    verificatieTelefoon:    z.string().trim().max(40).nullable().optional(),

    notes: z.string().trim().max(2000).nullable().optional(),

    /** Verplichte reden zodra een auto/berekend veld handmatig wordt aangepast (Deel 8). */
    reden: z.string().trim().min(1).max(500).optional(),
  })
  .strict()
export type UpdateWgvVeldenInput = z.infer<typeof UpdateWgvVeldenSchema>

/**
 * POST /werkgeversverklaringen/:id/variabele-controle — per variabele
 * inkomenscomponent: meenemen ja/nee + optioneel een gecorrigeerd bedrag + reden
 * (Deel 5 / FO-122). `veldSleutel` is een van de variabele-inkomsten-keys
 * (onregelmatigheidstoeslag, structureelOverwerk, structureleProvisie,
 * structureleReisuren, flexibelBudget). Een bedragwijziging vereist een reden.
 */
export const WgvVariabeleControleSchema = z.object({
  controles: z
    .array(
      z.object({
        veldSleutel: z.string().trim().min(1).max(64),
        meenemen:    z.boolean(),
        /** Optioneel gecorrigeerd 12-maands-bedrag; bij wijziging is een reden vereist. */
        bedrag:      moneyInput.nullable().optional(),
        reden:       z.string().trim().max(500).optional(),
      }),
    )
    .min(1),
})
export type WgvVariabeleControleInput = z.infer<typeof WgvVariabeleControleSchema>

/** POST /werkgeversverklaringen/:id/genereer — genereer de PDF. */
export const WgvGenereerSchema = z
  .object({
    /** Negeer zachte waarschuwingen (harde validaties blijven blokkerend). */
    negeerWaarschuwingen: z.boolean().optional(),
    /** FO-459 (batch-62) - briefpapierkeuze uit de kiezer; leeg = cascade. */
    briefpapier: BriefpapierKeuzeInvoerSchema.nullable().optional(),
  })
  .strict()
export type WgvGenereerInput = z.infer<typeof WgvGenereerSchema>

/** FO-459 (batch-62) - POST /werkgeversverklaringen/:id/preview: dezelfde keuze als genereren. */
export const WgvPreviewSchema = z
  .object({
    briefpapier: BriefpapierKeuzeInvoerSchema.nullable().optional(),
  })
  .strict()
export type WgvPreviewInput = z.infer<typeof WgvPreviewSchema>

/**
 * POST /werkgeversverklaringen/:id/onderteken — handmatig of elektronisch.
 * Bij ELEKTRONISCH is `signatureData` (base64 PNG van het signature-canvas)
 * verplicht; bij HANDMATIG niet (upload van een gescande versie loopt apart).
 */
export const WgvOndertekenSchema = z
  .object({
    methode:       z.enum(WGV_ONDERTEKEN_METHODE),
    /** Base64 PNG (data-URL of kale base64) — verplicht bij ELEKTRONISCH zonder opgeslagen handtekening. */
    signatureData: z.string().min(1).optional(),
    plaats:        z.string().trim().max(120).optional(),
    /**
     * batch-68 handtekening (FO-345 §6) — teken met de opgeslagen handtekening
     * van de ondertekenaar (bewuste klik; de server leest een kopie).
     */
    opgeslagenHandtekening: z.boolean().optional(),
    /** batch-68 — een nieuwe tekening ook opslaan als "mijn handtekening". */
    opslaanAlsStandaard: z.boolean().optional(),
    /** batch-68 (review K2) — het aantal streken uit het tekenvak (kwaliteitscheck). */
    aantalStreken: z.number().int().min(0).max(10000).optional(),
    /** batch-68 — verhoogde bevestiging (POST /documenten/:pdfDocumentId/herauth). */
    herauthToken: z.string().min(10).max(2000).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.methode === 'ELEKTRONISCH' && !val.signatureData && !val.opgeslagenHandtekening) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['signatureData'],
        message: 'Handtekening is verplicht bij elektronisch ondertekenen',
      })
    }
  })
export type WgvOndertekenInput = z.infer<typeof WgvOndertekenSchema>

/** POST /werkgeversverklaringen/:id/verstrek — verstrek aan de medewerker. */
export const WgvVerstrekSchema = z
  .object({
    kanaal: z.enum(WGV_VERSTREK_KANAAL),
    /**
     * batch-45 (FO-385) — bij kanaal DOWNLOAD/EMAIL: maak de verklaring óók in
     * de portal zichtbaar (aangevinkt in de verstrek-dialog). Kanaal PORTAL
     * maakt de verklaring per definitie portal-zichtbaar; daar is dit veld
     * overbodig. Default false: download/e-mail laten de portal ongemoeid.
     */
    ookInPortal: z.boolean().optional(),
  })
  .strict()
export type WgvVerstrekInput = z.infer<typeof WgvVerstrekSchema>

/** POST /portal/werkgeversverklaring-aanvragen — aanvraag door medewerker (Deel 12). */
export const CreateWgvAanvraagSchema = z.object({
  doel:          z.enum(WGV_AANVRAAG_DOEL),
  gewensteDatum: isoDate.optional(),
  opmerking:     z.string().trim().max(1000).optional(),
})
export type CreateWgvAanvraagInput = z.infer<typeof CreateWgvAanvraagSchema>

// ─── FO-122: NHG-PDF-template-beheer ──────────────────────────────────────────

/** Base64 van een geuploade PDF (ruime bovengrens tegen misbruik; NHG-PDF ~180KB). */
const pdfBase64 = z.string().min(1).max(15_000_000)

/** POST /werkgeversverklaringen/templates/analyse — detecteer AcroForm-velden. */
export const AnalyseWgvTemplateSchema = z.object({ pdfBase64 })
export type AnalyseWgvTemplateInput = z.infer<typeof AnalyseWgvTemplateSchema>

/** POST /werkgeversverklaringen/templates — upload + activeer een nieuwe NHG-PDF-versie. */
export const UploadWgvTemplateSchema = z.object({
  pdfBase64,
  versie:      z.string().trim().min(1).max(40),
  geldigVanaf: isoDate,
})
export type UploadWgvTemplateInput = z.infer<typeof UploadWgvTemplateSchema>

/** Eén template-versie in het beheer-overzicht. */
export interface WgvTemplateDTO {
  id: string
  naam: string
  versie: string
  templateType: 'DOCX' | 'PDF_ACROFORM'
  templateFileName: string
  /** ISO datetime. */
  geldigVanaf: string
  geldigTot: string | null
  actief: boolean
  createdAt: string
}

/**
 * Resultaat van een AcroForm-veld-analyse bij upload: de gedetecteerde velden,
 * welke gemapte velden ONTBREKEN (blokkeren activatie) en welke PDF-velden extra
 * zijn t.o.v. de mapping (informatief).
 */
export interface WgvTemplateAnalyseDTO {
  fieldCount: number
  detected: string[]
  missing: string[]
  extra: string[]
}

// ─── DTO's voor API-responses ─────────────────────────────────────────────────

/**
 * Lijst-rij voor het HR-overzicht (`/hrm/werkgeversverklaringen`). Bewust géén
 * salaris/lening/loonbeslag-bedragen — die horen niet in een lijstweergave.
 */
export interface WgvListRowDTO {
  id: string
  employeeId: string
  employeeNaam: string | null
  employerEntityId: string
  employerEntityNaam: string | null
  status: WgvStatus
  ondertekenaarNaam: string | null
  /** ISO datetime. */
  createdAt: string
  signedAt: string | null
  issuedAt: string | null
  /** Heeft de verklaring een gegenereerde PDF? */
  heeftPdf: boolean
}

/** Eén veld-herkomst-rij (controlescherm + veld-overzicht). */
export interface WgvVeldSnapshotDTO {
  veldSleutel: string
  bron: WgvVeldBron
  /** Auto/berekende waarde. Bij gevoelige velden serverside genulld indien geen inzage. */
  origineleWaarde: string | null
  definitieveWaarde: string | null
  handmatigGewijzigd: boolean
  wijzigingReden: string | null
  /** 12-maands-bedrag (variabele inkomsten) als string; genulld bij geen inzage. */
  bedrag12Maanden: string | null
  meenemen: boolean | null
  gecontroleerd: boolean
}

/** Eén audittrail-regel (alleen met auditView). Salarisbedragen nooit aanwezig. */
export interface WgvAuditLogDTO {
  id: string
  actie: WgvAuditActie
  /** Rauwe userId; read-endpoint resolvet naar naam (actorNaam ?? actor). */
  actor: string
  actorNaam: string | null
  veldSleutel: string | null
  oudeWaarde: string | null
  nieuweWaarde: string | null
  reden: string | null
  ipAddress: string | null
  createdAt: string
}

export interface WgvVerstrekkingDTO {
  id: string
  kanaal: WgvVerstrekKanaal
  verstrektDoor: string
  verstrektDoorNaam: string | null
  ontvangerEmployeeId: string
  createdAt: string
}

/**
 * Detail-DTO. Gevoelige velden (inkomen, lening, loonbeslag) zijn OPTIONEEL en
 * nullable zodat {@link redactWgvDetail} ze kan weglaten/nullen wanneer de
 * viewer geen inzage heeft. Geld als string (Decimal serialized).
 */
export interface WgvDetailDTO {
  id: string
  employeeId: string
  employeeNaam: string | null
  employerEntityId: string
  employerEntityNaam: string | null
  templateId: string
  status: WgvStatus

  // --- Aard dienstverband ---
  contractType: string
  contractEinddatum: string | null
  isFlexibel: boolean
  flexibelToelichting: string | null
  proeftijd: boolean
  proeftijdVerstreken: boolean | null
  reorganisatieVoornemen: boolean
  reorganisatieToelichting: string | null
  dgaJaNee: boolean
  dgaPercentage: string | null

  // --- Voortzetting ---
  voortzetting: WgvVoortzetting | null
  voortzettingMinMaanden: number | null
  voorwaardenGewijzigd: boolean
  voorwaardenToelichting: string | null

  // --- Inkomen (gevoelig; nullable + optioneel voor redactie) ---
  brutoMaandsalaris?: string | null
  brutoJaarsalaris?: string | null
  vakantietoeslag?: string | null
  dertiendeMaand?: string | null
  eindejaarsuitkering?: string | null
  onregelmatigheidstoeslag?: string | null
  structureelOverwerk?: string | null
  structureleProvisie?: string | null
  structureleReisuren?: string | null
  flexibelBudget?: string | null

  // --- Leningen en loonbeslag (gevoelig; nullable + optioneel) ---
  werkgeversleningJaNee?: boolean | null
  leningIngangsdatum?: string | null
  leningHoofdsom?: string | null
  leningLooptijdMaanden?: number | null
  leningMaandlast?: string | null
  loonbeslagJaNee?: boolean | null
  loonbeslagBedragPerMaand?: string | null
  loonbeslagToelichting?: string | null

  // --- Ondertekening / verificatie ---
  ondertekenaarNaam: string | null
  ondertekenaarUserId: string | null
  plaatsOndertekening: string | null
  ondertekenMethode: WgvOndertekenMethode | null
  verificatieContactNaam: string | null
  verificatieTelefoon: string | null

  // --- Workflow ---
  signedAt: string | null
  issuedAt: string | null
  reviewedOp: string | null
  notes: string | null

  // --- Resultaat ---
  pdfDocumentId: string | null
  /**
   * batch-68 handtekening (FO-345 §3.2) - bevestigingsniveau van het
   * elektronisch tekenen (instelling WERKGEVERSVERKLARING, standaard VERHOOGD:
   * eerst opnieuw inloggen). Optioneel: oudere antwoorden kennen het niet.
   */
  ondertekenBevestiging?: 'STANDAARD' | 'VERHOOGD'

  /**
   * NIET-monetaire herkomst-/dekkingsinfo van de variabele-inkomen-berekening
   * uit de loonstroken (hoeveel periodes gedekt, waarschuwingen). Geen bedragen.
   */
  inkomenBronMeta?: WgvInkomenBronMeta | null

  createdAt: string
  updatedAt: string

  snapshots: WgvVeldSnapshotDTO[]
}

/**
 * Eén per-periode regel van de onderbouwing van één variabele inkomenscomponent:
 * het bedrag (en aantal/uren indien op de strook) van die component in die periode.
 */
export interface WgvInkomenBerekeningRegel {
  /** "YYYY-MM" (MONTH) of "YYYY-Pnn" (FOUR_WEEK). */
  periode: string
  /** Aantal/uren van de component in die periode, of null. */
  aantal: number | null
  /** Bedrag van de component in die periode (euro). */
  bedrag: number
}

/**
 * Onderbouwing van één geannualiseerde variabele inkomenscomponent: de
 * per-periode regels + de som + de annualisatie (som / aantalPeriodes x
 * periodesPerJaar = geannualiseerd). `aantalPeriodes` is de DEKKING (alle
 * meegenomen periodes in het venster), niet enkel de periodes met een bedrag:
 * een maand zonder dit component telt als EUR 0 en drukt het gemiddelde.
 */
export interface WgvInkomenBerekening {
  veldSleutel: string
  regels: WgvInkomenBerekeningRegel[]
  som: number
  aantalPeriodes: number
  periodesPerJaar: number
  geannualiseerd: number
}

/**
 * Herkomst + dekking + onderbouwing van de variabele-inkomen-berekening (uit de
 * loonstroken). Bevat euro-bedragen (de per-component `berekeningen`) en is dus
 * GEVOELIG: `redactWgvDetail` nult dit veld bij geen salaris-inzage, net als de
 * inkomensbedragen zelf.
 */
export interface WgvInkomenBronMeta {
  bron: 'LOONSTROKEN'
  methode: 'PERIODE_SOM_GEANNUALISEERD'
  periodesGevonden: number
  periodesPerJaar: 12 | 13
  maandenGedekt: string[]
  perCategoriePeriodes?: Record<string, number>
  waarschuwingen: string[]
  /** Per variabele component de volledige, navolgbare berekening. */
  berekeningen?: WgvInkomenBerekening[]
}

/** Portal-aanvraag-rij. */
export interface WgvAanvraagDTO {
  id: string
  employeeId: string
  employeeNaam: string | null
  doel: WgvAanvraagDoel
  gewensteDatum: string | null
  opmerking: string | null
  afgehandeld: boolean
  createdAt: string
}
